import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { tp_small } from "../../constants/icons";
import { formatGroupedNumber, formatCurrencyWithSymbol } from "../../utils/currencyFormatter";
import {
  POSITION_HEADER_SUFFIX,
  POSITION_HEADER_TOOLTIP,
  getPositionDisplay,
} from "../../utils/positionFormatter";
import NationalityFlags from "../common/NationalityFlags";
import { ROLES } from "../../config/roles";
import messageRequestService from "../../services/messageRequestApi";
import { RequestPendingBadge } from "../inbox/MessageRequestComposeModal";
import FifaVerifiedBadge, { isFifaVerified } from "../common/FifaVerifiedBadge";
// Competitions search rows reuse the shared competition-logo fallback (image
// → flag/country-code chip), extracted from the StandingsWidget.
import { CompetitionLogo } from "../common/standingsVisuals";
const SearchTable = ({ type, data, loading }) => {
  const navigate = useNavigate();
  const currentRole = useSelector((state) => state.auth?.currentRole);

  useEffect(() => {
    console.log("data", data);
  }, [data]);

  // Design 1l — the SAME quiet, terminal "Request pending" chip the agent profile
  // shows, now on the agent SEARCH rows the frame is titled after ("AGENT SEARCH").
  // Source of truth is the one the profile already uses: the sender's own sent
  // list, fetched ONCE per result set and matched locally, never one call per row.
  // The chip only ever flips to a chat when the other side accepts — it never
  // turns into "declined" and never disappears with a signal (Invariant 9).
  const [pendingAgentIds, setPendingAgentIds] = useState(() => new Set());
  const isAgentViewer = currentRole === ROLES.Agent;
  const showsAgents = type === "agent" || type === "agents";

  useEffect(() => {
    if (!isAgentViewer || !showsAgents) {
      setPendingAgentIds(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const res = await messageRequestService.listSent();
      if (cancelled || !res?.success) return;
      const rows = res.results || res.data?.results || [];
      setPendingAgentIds(
        new Set(
          rows
            .filter((r) => r?.status !== "accepted")
            .map((r) => r?.recipient_agent_id ?? r?.recipient?.agent_id)
            .filter((id) => id != null)
            .map((id) => String(id)),
        ),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [isAgentViewer, showsAgents]);

  const handlePlayerClick = (playerId) => {
    navigate(`/search/player/${playerId}`);
  };

  const handleClubClick = (clubId) => {
    navigate(`/search/club/${clubId}`);
  };

  const handleStaffClick = (staffId) => {
    navigate(`/search/staff/${staffId}`);
  };

  const handleAgentClick = (agentId) => {
    navigate(`/search/agent/${agentId}`);
  };

  const handleAgencyClick = (agencyId) => {
    navigate(`/search/agency/${agencyId}`);
  };

  const handleCoachClick = (coachId) => {
    navigate(`/search/coach/${coachId}`);
  };

  const handleCompetitionClick = (competitionId) => {
    navigate(`/competitions/${competitionId}`);
  };

  if (loading) {
    return <div className="animate-pulse bg-white rounded-lg h-96" />;
  }

  const renderPlayerRow = (player) => (
    <tr
      key={player.wyscout_id}
      className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
      onClick={() => handlePlayerClick(player.platform_id || player.wyscout_id)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-4">
          {player.image_url ? (
            <div className="relative sm:w-10 sm:h-10 w-8 h-8 rounded-xl object-cover">
              <img
                src={player.image_url}
                alt=""
                className="w-full h-full object-cover rounded-xl border border-gray-200"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div
                className="sm:w-10 sm:h-10 w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 font-medium"
                style={{ display: "none" }}
              >
                {(player.short_name || player.name || "-")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </div>
              {player?.source === "platform" && (
                <img
                  src={tp_small}
                  alt=""
                  className="absolute bottom-0 right-0 w-4 h-4 object-cover"
                />
              )}
            </div>
          ) : (
            <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
              {(player.short_name || player.name || "-")
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
          )}
          <div>
            {/* Display rule (Marin, Staging-Abnahme 2026-08-13): prefer the
                short name ("Bernardo Silva"), the full legal name ("Bernardo
                Mota Veiga de Carvalho e Silva") is only the fallback. */}
            <h4 className="font-bold text-xs sm:text-[16px] text-[#222A35]">
              {player.short_name || player.name}
            </h4>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {getPositionDisplay(player, player?.position || "-")}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {player.club ? (
          <div className="flex items-center gap-2">
            {player.club_image ? (
              <img
                src={player.club_image}
                alt=""
                className="sm:w-6 sm:h-6 w-5 h-5 rounded-full object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="sm:w-6 sm:h-6 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500"
              style={{ display: player.club_image ? "none" : "flex" }}
            >
              {(player.club?.name || "-").charAt(0)}
            </div>
            <span className="text-xs sm:text-[16px] text-[#222A35]">
              {player.club?.name || "-"}
            </span>
          </div>
        ) : (
          <span className="text-xs sm:text-[16px] text-[#222A35]">-</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {/* All citizenships, national team first (see NationalityFlags). */}
        <NationalityFlags
          nationalities={player}
          className="sm:w-5 sm:h-4 w-4 h-3 object-cover border border-gray-200 rounded-sm"
        />
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {player.age ? player.age : "-"} /{" "}
        {player.date_of_birth ? player.date_of_birth : "-"}
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {player.market_value
          ? // Always the symbol, never the raw code — "EUR12.000.000" read
            // like a bug (Marin, staging acceptance 2026-08-14). The shared
            // formatter maps EUR->"€" and keeps exotic codes correct.
            formatCurrencyWithSymbol(player.market_value, player.market_currency || "EUR")
          : "-"}
      </td>
    </tr>
  );

  const renderAgentRow = (agent) => (
    <tr
      key={agent.id}
      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
      onClick={() => handleAgentClick(agent.account_id)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-4">
          {agent.profile_picture_url ? (
            <img
              src={agent.profile_picture_url}
              alt=""
              className="sm:w-10 sm:h-10 w-8 h-8 rounded-full object-cover border border-gray-200"
            />
          ) : (
            <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
              {(agent.name || "-")
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
          )}
          <span className="font-bold text-xs sm:text-[16px] text-[#222A35]">
            {agent.name}
          </span>
          {/* D-A: icon-only FIFA Verified badge in rows — renders ONLY on
              license_verification_status === 'fifa_verified', never from
              the self-declared licence number column. */}
          {isFifaVerified(agent) && <FifaVerifiedBadge size="sm" />}
          {/* 1l: the terminal pending chip sits with the person it belongs to. */}
          {pendingAgentIds.has(String(agent.id)) && (
            <RequestPendingBadge compact />
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {agent?.agency_profile_picture_url ? (
            <img
              src={agent.agency_profile_picture_url}
              alt=""
              className="sm:w-10 sm:h-10 w-8 h-8 rounded-full object-cover border border-gray-200"
            />
          ) : (
            <div className="sm:w-6 sm:h-6 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
              {(agent?.agency?.name || "-").charAt(0)}
            </div>
          )}
          <span className="text-xs sm:text-[16px] text-[#222A35]">
            {agent?.agency?.name || "-"}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {agent?.country?.image_url ? (
            <img
              src={agent.country.image_url}
              alt=""
              className="sm:w-6 sm:h-4 w-5 h-3 border border-gray-200 rounded-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : agent?.countryCode ? (
            <img
              src={`https://flagcdn.com/${agent.countryCode.toLowerCase()}.svg`}
              alt=""
              className="sm:w-6 sm:h-4 w-5 h-3 border border-gray-200 rounded-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          <span className="text-xs sm:text-[16px] text-[#222A35]">
            {(typeof agent?.country === "object"
              ? agent?.country?.name
              : agent?.country) || "-"}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {agent?.license_number || "-"}
      </td>
    </tr>
  );

  const renderClubRow = (club) => (
    <tr
      key={club.wyscout_id}
      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
      onClick={() => handleClubClick(club.wyscout_id)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 sm:h-10 sm:w-10 h-8 w-8 flex items-center justify-center bg-gray-100 rounded">
            {club.image_url || club.image_path ? (
              <div className="relative sm:w-10 sm:h-10 w-8 h-8 rounded-xl object-cover">
                <img
                  src={club.image_url || club.image_path}
                  alt=""
                  className="w-full h-full object-cover rounded-xl border border-gray-200"
                />
                {club?.source === "platform" && (
                  <img
                    src={tp_small}
                    alt=""
                    className="absolute bottom-0 right-0 w-4 h-4 object-cover"
                  />
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-400">
                {club.name.charAt(0) || club.official_name.charAt(0) || "-"}
              </span>
            )}
          </div>
          <div className="ml-4">
            <div className="font-bold text-xs sm:text-[16px] text-[#222A35]">
              {club.name || club.official_name || "-"}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-[16px] text-[#222A35]">
        {club.division?.name || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <img
            src={club.area?.image_url || club.country?.image_url || ""}
            alt=""
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
            className="sm:w-6 sm:h-4 w-5 h-3 object-cover border border-gray-200 rounded-sm"
          />
          <span className="text-xs sm:text-[16px] text-[#222A35]">
            {club.area?.name || club.country?.name || "-"}
          </span>
        </div>
      </td>
    </tr>
  );

  const renderAgencyRow = (agency) => {
    return (
      <tr
        key={agency.id}
        className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
        onClick={() => handleAgencyClick(agency.agency_id || agency.id)}
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex-shrink-0 sm:h-10 sm:w-10 h-8 w-8 flex items-center justify-center bg-gray-100 rounded">
              {agency.profile_picture_url &&
              agency.profile_picture_url !==
                "https://cdn.sportmonks.com/images/soccer/team_placeholder.png" ? (
                <div className="relative sm:w-10 sm:h-10 w-8 h-8 rounded-xl  object-cover">
                  <img
                    src={agency.profile_picture_url}
                    alt=""
                    className="w-full h-full object-cover rounded-xl border border-gray-200 "
                  />
                  <img
                    src={tp_small}
                    alt=""
                    className="absolute bottom-0 right-0 w-4 h-4 object-cover"
                  />
                </div>
              ) : (
                <span className="text-xs text-gray-400">-</span>
              )}
            </div>
            <div className="ml-4">
              <div className="font-bold text-xs sm:text-[16px] text-[#222A35]">
                {agency.name || "-"}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-xs sm:text-[16px] text-[#222A35]">
          <div className="flex items-center gap-2">
            {agency?.country?.image_url ? (
              <img
                src={agency.country.image_url}
                alt=""
                className="sm:w-6 sm:h-4 w-5 h-3 object-cover border border-gray-200 rounded-sm"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : null}
            <span>
              {(typeof agency?.country === "object"
                ? agency?.country?.name
                : agency?.country) || "-"}
            </span>
          </div>
        </td>
      </tr>
    );
  };

  const renderStaffRow = (staff) => (
    <tr
      key={staff.id}
      className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
      onClick={() => handleStaffClick(staff?.account_id)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-4">
          {staff.document_url ? (
            <div className="relative sm:w-10 sm:h-10 w-8 h-8 rounded-xl object-cover">
              <img
                src={staff.document_url}
                alt=""
                className="w-full h-full object-cover rounded-xl border border-gray-200"
              />
              {staff?.is_platform_user && (
                <img
                  src={tp_small}
                  alt=""
                  className="absolute bottom-0 right-0 w-4 h-4 object-cover"
                />
              )}
            </div>
          ) : (
            <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
              {(staff.name || "-")
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
          )}
          <div>
            <h4 className="font-bold text-xs sm:text-[16px] text-[#222A35]">
              {staff.name}
            </h4>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {(typeof staff?.staff_type === "object"
          ? staff?.staff_type?.name
          : staff?.staff_type) ||
          (typeof staff?.role_type === "object"
            ? staff?.role_type?.name
            : staff?.role_type) ||
          "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {staff.works_for_club ? (
          <div className="flex items-center gap-2">
            <div className="sm:w-6 sm:h-6 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
              Club
            </div>
            <span className="text-xs sm:text-sm text-gray-600">Club Staff</span>
          </div>
        ) : (
          <span className="text-xs sm:text-[16px] text-[#222A35]">-</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {staff?.country?.image_url ? (
            <img
              src={staff.country.image_url}
              alt=""
              className="sm:w-5 sm:h-4 w-4 h-3 object-cover border border-gray-200 rounded-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : staff?.countryCode ? (
            <img
              src={`https://flagcdn.com/${String(
                staff.countryCode
              ).toLowerCase()}.svg`}
              alt=""
              className="sm:w-5 sm:h-4 w-4 h-3 object-cover border border-gray-200 rounded-sm"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          <span className="text-xs sm:text-[16px] text-[#222A35]">
            {(typeof staff?.country === "object"
              ? staff?.country?.name
              : staff?.country) || "-"}
          </span>
        </div>
      </td>
    </tr>
  );

  const renderCoachRow = (coach) => {
    // Ensure we have a valid key (string or number)
    const rowKey = coach.ts_coach_id || coach.id || (typeof coach.id === 'object' ? coach.id?.id : null) || Math.random();
    
    return (
    <tr
      key={typeof rowKey === 'object' ? JSON.stringify(rowKey) : String(rowKey)}
      className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
      onClick={() => handleCoachClick(coach.public_coach_id || coach.ts_coach_id || (typeof coach.id === 'object' ? coach.id?.id : coach.id))}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-4">
          {(coach.logo || coach.image_path) ? (
            <div className="relative sm:w-10 sm:h-10 w-8 h-8 rounded-xl object-cover">
              <img
                src={coach.logo || coach.image_path}
                alt=""
                className="w-full h-full object-cover rounded-xl border border-gray-200"
              />
              {coach?.is_platform_user && (
                <img
                  src={tp_small}
                  alt=""
                  className="absolute bottom-0 right-0 w-4 h-4 object-cover"
                />
              )}
            </div>
          ) : (
            <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
              {(coach.name || coach.display_name || "-")
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>
          )}
          <div>
            <h4 className="font-bold text-xs sm:text-[16px] text-[#222A35]">
              {coach.name || coach.display_name}
            </h4>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {(() => {
          const teamName = typeof coach.team === 'object' 
            ? coach.team?.name 
            : coach.team_name;
          const teamLogo = typeof coach.team === 'object'
            ? coach.team?.logo || coach.team?.image_path
            : coach.team_logo;
          
          return teamName ? (
            <div className="flex items-center gap-2">
              {teamLogo && (
                <img
                  src={teamLogo}
                  alt=""
                  className="sm:w-6 sm:h-6 w-5 h-5 rounded-full object-cover border border-gray-200"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <span className="text-xs sm:text-[16px] text-[#222A35]">
                {teamName}
              </span>
            </div>
          ) : (
            <span className="text-xs sm:text-[16px] text-[#222A35]">-</span>
          );
        })()}
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        <div className="flex items-center gap-2">
          {(() => {
            const nationalityImage = typeof coach?.nationality === 'object' 
              ? coach?.nationality?.image_url 
              : coach?.nationality_image;
            const nationalityName = typeof coach?.nationality === 'object'
              ? coach?.nationality?.name
              : coach?.nationality;
            
            return (
              <>
                {nationalityImage && (
                  <img
                    src={nationalityImage}
                    alt=""
                    className="sm:w-5 sm:h-4 w-4 h-3 object-cover border border-gray-200 rounded-sm"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <span>{nationalityName || "-"}</span>
              </>
            );
          })()}
        </div>
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {coach.age || (coach.date_of_birth ? calculateAge(coach.date_of_birth) : "-")}
      </td>
      <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
        {Array.isArray(coach.preferred_formation) 
          ? coach.preferred_formation[0] || "-"
          : coach.preferred_formation || "-"}
      </td>
    </tr>
    );
  };

  // Competitions (TP-CompetitionPage build). BE shape (_search_competitions):
  // { id, name (resolver display name — never overridden FE-side),
  //   short_name, logo, kind ("league"/"cup"/"friendly"/""),
  //   country: { id, name, logo } | null }. Empty renders nothing (no "-").
  const renderCompetitionRow = (comp) => {
    const id = comp.id;
    const name = comp.name || comp.short_name;
    if (!id || !name) return null;
    const kindLabel = comp.kind
      ? comp.kind.charAt(0).toUpperCase() + comp.kind.slice(1)
      : "";
    return (
      <tr
        key={id}
        className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
        onClick={() => handleCompetitionClick(id)}
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-4">
            <CompetitionLogo league={{ logo: comp.logo }} size={32} />
            <span className="text-xs sm:text-[16px] font-medium text-[#222A35]">
              {name}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
          <div className="flex items-center gap-2">
            {comp.country?.logo && (
              <img
                src={comp.country.logo}
                alt=""
                className="w-4 h-3 object-cover rounded-[1px]"
              />
            )}
            <span>{comp.country?.name || ""}</span>
          </div>
        </td>
        <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] whitespace-nowrap">
          {kindLabel}
        </td>
      </tr>
    );
  };

  const renderTableHeader = () => {
    switch (type) {
      case "players":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Player
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              <span
                className="inline-flex items-center gap-1"
                title={POSITION_HEADER_TOOLTIP}
              >
                <span className="uppercase">Position</span>
                <span className="text-[10px] text-[#667085] font-normal uppercase">
                  {POSITION_HEADER_SUFFIX}
                </span>
              </span>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Club
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Country
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Age/DOB
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Market Value
            </th>
          </tr>
        );
      case "agents":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Agent
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Agency
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Country
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              License No.
            </th>
          </tr>
        );
      case "clubs":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Club
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Competition
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Country
            </th>
          </tr>
        );
      case "agencies":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Agency Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Country
            </th>
          </tr>
        );
      case "staff":
      case "staffs":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Staff
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Position
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Club
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Nationality
            </th>
          </tr>
        );
      case "competitions":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Competition
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Country
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Type
            </th>
          </tr>
        );
      case "coaches":
        return (
          <tr className="bg-[#F7F7F7]">
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Coach
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Team
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Nationality
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Age
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
              Formation
            </th>
          </tr>
        );
      default:
        return null;
    }
  };

  const renderTableRows = () => {
    // Ensure data is always an array
    let tableData = Array.isArray(data) ? data : [];
    
    // Validate and clean data to prevent rendering objects directly
    tableData = tableData.map((item) => {
      if (!item || typeof item !== 'object') return item;
      
      // Ensure all string fields are actually strings, not objects
      const cleaned = { ...item };
      
      // Handle nationality field - convert object to string if needed
      if (cleaned.nationality && typeof cleaned.nationality === 'object') {
        cleaned.nationality = cleaned.nationality.name || cleaned.nationality;
      }
      
      return cleaned;
    });

    if (!tableData || tableData.length === 0) {
      const colSpan =
        type === "players"
          ? 6
          : type === "agents"
          ? 4
          : type === "clubs"
          ? 3
          : type === "staff" || type === "staffs"
          ? 4
          : type === "coaches"
          ? 6
          : type === "agencies"
          ? 2
          : type === "competitions"
          ? 3
          : 1;
      return (
        <tr>
          <td colSpan={colSpan} className="px-6 py-4 text-center text-gray-500">
            -
          </td>
        </tr>
      );
    }

    switch (type) {
      case "players":
        return tableData.map(renderPlayerRow);
      case "agents":
        return tableData.map(renderAgentRow);
      case "clubs":
        return tableData.map(renderClubRow);
      case "agencies":
        return tableData.map(renderAgencyRow);
      case "staff":
      case "staffs":
        return tableData.map(renderStaffRow);
      case "coaches":
        return tableData.map(renderCoachRow);
      case "competitions":
        return tableData.map(renderCompetitionRow);
      default:
        return null;
    }
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to format numbers. Grouping comes from the platform's one
  // helper (apostrophe, locale-independent — Marin, 2026-08-02) instead of a
  // local en-US comma, so a market value in this table reads the same as the
  // same figure on the player's own page.
  const formatNumber = (num) => formatGroupedNumber(num);

  return (
    <div className="relative px-4 sm:px-6 lg:px-12">
      {loading ? (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto border border-[#D6DBEA]">
          <table className="min-w-full divide-y divide-gray-200 table-sticky-first">
            <thead className="bg-[#F7F7F7]">{renderTableHeader()}</thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {renderTableRows()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SearchTable;
